<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import { useAlert } from 'dashboard/composables';
import BoardsAPI from 'dashboard/api/boards';
import ContactAPI from 'dashboard/api/contacts';
import ContactNotesAPI from 'dashboard/api/contactNotes';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  boardId: { type: Number, required: true },
  stageId: { type: Number, default: null },
  deal: { type: Object, default: null },
});

const emit = defineEmits(['close', 'saved']);
const router = useRouter();
const store = useStore();
const { t } = useI18n();

function contactLabel(contact) {
  if (!contact) return '';
  return [
    contact.name,
    contact.email || contact.phone_number || contact.identifier,
  ]
    .filter(Boolean)
    .join(t('KANBAN.SEPARATOR'));
}

const isEditing = computed(() => !!props.deal);
const conversations = computed(() => props.deal?.conversations || []);
const primaryConversation = computed(() => conversations.value[0] || null);
const activeTab = ref('summary');

const name = ref(props.deal?.name || '');
const amountCents = ref(props.deal?.amount_cents || 0);
const priority = ref(props.deal?.priority || 'no_priority');
const contactId = ref(props.deal?.contact?.id || '');
const selectedContact = ref(props.deal?.contact || null);
const contactQuery = ref(contactLabel(props.deal?.contact));
const assigneeId = ref(props.deal?.assignee?.id || '');
const expectedCloseDate = ref(props.deal?.expected_close_date || '');
const isSaving = ref(false);

const contacts = ref([]);
const isSearchingContacts = ref(false);
const contactSearchError = ref('');
const showContactResults = ref(false);
let contactSearchTimer;

const notes = ref([]);
const noteContent = ref('');
const isFetchingNotes = ref(false);
const isCreatingNote = ref(false);

const contactLabels = ref([]);
const isSavingLabels = ref(false);

const accountLabels = computed(() => store.getters['labels/getLabels'] || []);
const agents = computed(() => store.getters['agents/getAgents'] || []);
const tabs = computed(() => [
  {
    key: 'summary',
    label: t('KANBAN.DEAL.TABS.SUMMARY'),
    icon: 'i-lucide-layout-dashboard',
  },
  {
    key: 'contact',
    label: t('KANBAN.DEAL.TABS.CONTACT'),
    icon: 'i-lucide-user-round',
  },
  {
    key: 'notes',
    label: t('KANBAN.DEAL.TABS.NOTES'),
    icon: 'i-lucide-notebook-pen',
    count: notes.value.length,
  },
  {
    key: 'activity',
    label: t('KANBAN.DEAL.TABS.ACTIVITY'),
    icon: 'i-lucide-history',
    count: conversations.value.length,
  },
]);

const amountDisplay = computed({
  get: () => (amountCents.value / 100).toFixed(2),
  set: val => {
    amountCents.value = Math.round(parseFloat(val || 0) * 100);
  },
});

const formatCurrency = cents =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format((cents || 0) / 100);

const formatTimestamp = timestamp => {
  if (!timestamp) return '';
  return shortTimestamp(dynamicTime(timestamp));
};

const contactInitial = computed(
  () => selectedContact.value?.name?.[0]?.toUpperCase() || '?'
);

const fetchContactLabels = async () => {
  if (!contactId.value) return;
  try {
    const { data } = await ContactAPI.getContactLabels(contactId.value);
    contactLabels.value = data.payload || data || [];
  } catch {
    contactLabels.value = [];
  }
};

const fetchNotes = async () => {
  if (!contactId.value) return;
  isFetchingNotes.value = true;
  try {
    const { data } = await ContactNotesAPI.get(contactId.value);
    notes.value = data || [];
  } catch {
    notes.value = [];
  } finally {
    isFetchingNotes.value = false;
  }
};

const searchContacts = query => {
  contactQuery.value = query;
  if (query !== contactLabel(selectedContact.value)) {
    selectedContact.value = null;
    contactId.value = '';
    contactLabels.value = [];
    notes.value = [];
  }
  contactSearchError.value = '';
  window.clearTimeout(contactSearchTimer);

  if (!query.trim() || query.trim().length < 2) {
    contacts.value = [];
    showContactResults.value = false;
    return;
  }

  contactSearchTimer = window.setTimeout(async () => {
    isSearchingContacts.value = true;
    showContactResults.value = true;
    try {
      const {
        data: { payload },
      } = await ContactAPI.search(query.trim(), 1, 'name');
      contacts.value = payload || [];
    } catch {
      contacts.value = [];
      contactSearchError.value = t('KANBAN.DEAL.CONTACT_SEARCH_ERROR');
    } finally {
      isSearchingContacts.value = false;
    }
  }, 250);
};

const selectContact = async contact => {
  selectedContact.value = contact;
  contactId.value = contact.id;
  contactQuery.value = contactLabel(contact);
  contacts.value = [];
  showContactResults.value = false;
  await Promise.all([fetchNotes(), fetchContactLabels()]);
};

const clearContact = () => {
  selectedContact.value = null;
  contactId.value = '';
  contactQuery.value = '';
  notes.value = [];
  contactLabels.value = [];
};

const saveDeal = async () => {
  if (!name.value.trim() || !contactId.value) {
    useAlert(t('KANBAN.DEAL.REQUIRED_FIELDS_ERROR'));
    return;
  }

  isSaving.value = true;
  try {
    const payload = {
      deal: {
        name: name.value.trim(),
        amount_cents: amountCents.value,
        priority: priority.value,
        contact_id: contactId.value,
        assignee_id: assigneeId.value || null,
        stage_id: props.stageId,
        expected_close_date: expectedCloseDate.value || null,
      },
    };

    if (isEditing.value) {
      await BoardsAPI.updateDeal(props.boardId, props.deal.id, payload);
    } else {
      await BoardsAPI.createDeal(props.boardId, payload);
    }
    emit('saved');
  } catch (e) {
    useAlert(e.message || t('KANBAN.DEAL.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const deleteDeal = async () => {
  if (!props.deal) return;
  try {
    await BoardsAPI.deleteDeal(props.boardId, props.deal.id);
    emit('saved');
  } catch {
    useAlert(t('KANBAN.DEAL.DELETE_ERROR'));
  }
};

const openConversation = conversation => {
  const conversationId = conversation.display_id || conversation.id;
  router.push({
    name: 'inbox_conversation',
    params: { conversation_id: conversationId },
  });
  emit('close');
};

const createNote = async () => {
  if (!contactId.value || !noteContent.value.trim()) return;
  isCreatingNote.value = true;
  try {
    const { data } = await ContactNotesAPI.create(
      contactId.value,
      noteContent.value.trim()
    );
    notes.value = [data, ...notes.value];
    noteContent.value = '';
  } catch {
    useAlert(t('KANBAN.DEAL.NOTE_SAVE_ERROR'));
  } finally {
    isCreatingNote.value = false;
  }
};

const toggleContactLabel = async labelTitle => {
  if (!contactId.value) return;
  isSavingLabels.value = true;
  try {
    const nextLabels = contactLabels.value.includes(labelTitle)
      ? contactLabels.value.filter(label => label !== labelTitle)
      : [...contactLabels.value, labelTitle];
    await ContactAPI.updateContactLabels(contactId.value, nextLabels);
    contactLabels.value = nextLabels;
  } catch {
    useAlert(t('KANBAN.DEAL.LABEL_SAVE_ERROR'));
  } finally {
    isSavingLabels.value = false;
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('labels/get');
  if (contactId.value) {
    fetchNotes();
    fetchContactLabels();
  }
});
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
    @click.self="emit('close')"
  >
    <section
      class="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg border border-n-weak bg-n-background shadow-xl"
      role="dialog"
      aria-modal="true"
      :aria-label="isEditing ? $t('KANBAN.DEAL.EDIT') : $t('KANBAN.DEAL.NEW')"
    >
      <header
        class="flex flex-wrap items-start justify-between gap-3 border-b border-n-weak px-5 py-4"
      >
        <div class="min-w-0">
          <p
            class="text-xs font-semibold uppercase tracking-wider text-n-slate-10"
          >
            {{ isEditing ? $t('KANBAN.DEAL.EDIT') : $t('KANBAN.DEAL.NEW') }}
          </p>
          <h2 class="mt-1 truncate text-lg font-semibold text-n-slate-12">
            {{ name || $t('KANBAN.DEAL.NAME_PLACEHOLDER') }}
          </h2>
          <div
            class="mt-2 flex flex-wrap items-center gap-2 text-sm text-n-slate-11"
          >
            <span class="inline-flex items-center gap-1">
              <span class="i-lucide-wallet size-4" />
              {{ formatCurrency(amountCents) }}
            </span>
            <span
              v-if="primaryConversation"
              class="inline-flex items-center gap-1"
            >
              <span class="i-lucide-message-square size-4" />
              {{
                $t('KANBAN.DEAL.CONVERSATION_ID', {
                  id: primaryConversation.display_id || primaryConversation.id,
                })
              }}
            </span>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <Button
            v-if="primaryConversation"
            type="button"
            size="sm"
            variant="faded"
            color="blue"
            icon="i-lucide-message-square"
            :label="$t('KANBAN.DEAL.OPEN_CONVERSATION')"
            @click="openConversation(primaryConversation)"
          />
          <button
            type="button"
            class="grid size-10 place-content-center rounded-md text-n-slate-11 transition-colors hover:bg-n-brand/5 hover:text-n-slate-12"
            :aria-label="$t('KANBAN.CANCEL')"
            @click="emit('close')"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>
      </header>

      <div class="border-b border-n-weak px-5">
        <div class="flex gap-1 overflow-x-auto">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            type="button"
            class="flex min-h-11 items-center gap-2 border-b-2 px-3 text-sm font-semibold transition-colors"
            :class="
              activeTab === tab.key
                ? 'border-n-brand text-n-slate-12'
                : 'border-transparent text-n-slate-10 hover:text-n-slate-12'
            "
            @click="activeTab = tab.key"
          >
            <span :class="tab.icon" class="size-4" />
            {{ tab.label }}
            <span
              v-if="tab.count"
              class="rounded-full border border-n-weak bg-transparent px-1.5 py-0.5 text-xs text-n-slate-11"
            >
              {{ tab.count }}
            </span>
          </button>
        </div>
      </div>

      <form class="min-h-0 overflow-y-auto" @submit.prevent="saveDeal">
        <div v-if="activeTab === 'summary'" class="space-y-5 p-5">
          <div
            class="flex flex-wrap items-center justify-between gap-3 border-b border-n-weak pb-4"
          >
            <div class="flex min-w-0 items-center gap-3">
              <img
                v-if="selectedContact?.thumbnail"
                :src="selectedContact.thumbnail"
                class="size-11 rounded-md border border-n-weak object-cover"
                alt=""
              />
              <div
                v-else
                class="grid size-11 flex-shrink-0 place-content-center rounded-md border border-n-weak bg-transparent text-sm font-semibold text-n-slate-12"
              >
                {{ contactInitial }}
              </div>
              <div class="min-w-0">
                <p class="truncate text-sm font-semibold text-n-slate-12">
                  {{
                    selectedContact?.name ||
                    $t('KANBAN.DEAL.NO_CONTACT_SELECTED')
                  }}
                </p>
                <p class="truncate text-sm text-n-slate-11">
                  {{
                    selectedContact?.email ||
                    selectedContact?.phone_number ||
                    selectedContact?.identifier ||
                    $t('KANBAN.DEAL.CONTACT_PLACEHOLDER')
                  }}
                </p>
              </div>
            </div>
            <Button
              v-if="primaryConversation"
              type="button"
              size="sm"
              variant="faded"
              color="blue"
              icon="i-lucide-external-link"
              :label="$t('KANBAN.DEAL.OPEN_CONVERSATION')"
              @click="openConversation(primaryConversation)"
            />
          </div>

          <div class="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2">
            <label class="block sm:col-span-2">
              <span class="text-sm font-medium text-n-slate-11">
                {{ $t('KANBAN.DEAL.NAME_LABEL') }}
              </span>
              <input
                v-model="name"
                type="text"
                class="mt-1 min-h-11 w-full rounded-md border border-n-weak bg-transparent px-4 py-2.5 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand/60 focus:bg-n-brand/5"
                :placeholder="$t('KANBAN.DEAL.NAME_PLACEHOLDER')"
              />
            </label>

            <label class="block">
              <span class="text-sm font-medium text-n-slate-11">
                {{ $t('KANBAN.DEAL.VALUE_LABEL') }}
              </span>
              <input
                v-model="amountDisplay"
                type="number"
                step="0.01"
                min="0"
                class="mt-1 min-h-11 w-full rounded-md border border-n-weak bg-transparent px-4 py-2.5 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand/60 focus:bg-n-brand/5"
                :placeholder="$t('KANBAN.DEAL.VALUE_PLACEHOLDER')"
              />
            </label>

            <label class="block">
              <span class="text-sm font-medium text-n-slate-11">
                {{ $t('KANBAN.DEAL.PRIORITY_LABEL') }}
              </span>
              <select
                v-model="priority"
                class="mt-1 min-h-11 w-full rounded-md border border-n-weak bg-transparent py-2.5 pl-4 pr-10 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand/60 focus:bg-n-brand/5"
              >
                <option value="no_priority">
                  {{ $t('KANBAN.DEAL.PRIORITY_NONE') }}
                </option>
                <option value="low">
                  {{ $t('KANBAN.DEAL.PRIORITY_LOW') }}
                </option>
                <option value="medium">
                  {{ $t('KANBAN.DEAL.PRIORITY_MEDIUM') }}
                </option>
                <option value="high">
                  {{ $t('KANBAN.DEAL.PRIORITY_HIGH') }}
                </option>
                <option value="urgent">
                  {{ $t('KANBAN.DEAL.PRIORITY_URGENT') }}
                </option>
              </select>
            </label>

            <label class="block">
              <span class="text-sm font-medium text-n-slate-11">
                {{ $t('KANBAN.DEAL.ASSIGNEE_LABEL') }}
              </span>
              <select
                v-model="assigneeId"
                class="mt-1 min-h-11 w-full rounded-md border border-n-weak bg-transparent py-2.5 pl-4 pr-10 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand/60 focus:bg-n-brand/5"
              >
                <option value="">
                  {{ $t('KANBAN.DEAL.ASSIGNEE_UNASSIGNED') }}
                </option>
                <option
                  v-for="agent in agents"
                  :key="agent.id"
                  :value="agent.id"
                >
                  {{ agent.name }}
                </option>
              </select>
            </label>

            <label class="block">
              <span class="text-sm font-medium text-n-slate-11">
                {{ $t('KANBAN.DEAL.CLOSE_DATE_LABEL') }}
              </span>
              <input
                v-model="expectedCloseDate"
                type="date"
                class="mt-1 min-h-11 w-full rounded-md border border-n-weak bg-transparent px-4 py-2.5 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand/60 focus:bg-n-brand/5"
              />
            </label>
          </div>
        </div>

        <div v-else-if="activeTab === 'contact'" class="space-y-5 p-5">
          <div class="relative max-w-4xl">
            <span class="text-sm font-medium text-n-slate-11">
              {{ $t('KANBAN.DEAL.CONTACT_LABEL') }}
            </span>
            <div
              class="mt-1 grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_128px] sm:items-stretch"
            >
              <input
                :value="contactQuery"
                type="text"
                class="min-h-11 min-w-0 flex-1 rounded-md border border-n-weak bg-transparent px-4 py-2.5 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand/60 focus:bg-n-brand/5"
                :placeholder="$t('KANBAN.DEAL.CONTACT_SEARCH_PLACEHOLDER')"
                autocomplete="off"
                @input="searchContacts($event.target.value)"
                @focus="showContactResults = contacts.length > 0"
              />
              <Button
                v-if="selectedContact"
                type="button"
                sm
                faded
                slate
                icon="i-lucide-x"
                class="min-h-11 w-full justify-center"
                :label="$t('KANBAN.DEAL.CLEAR_CONTACT')"
                :aria-label="$t('KANBAN.DEAL.CLEAR_CONTACT')"
                @click="clearContact"
              />
            </div>

            <div
              v-if="showContactResults"
              class="absolute left-0 right-0 top-full z-40 mt-2 max-h-72 overflow-y-auto rounded-lg border border-n-weak bg-n-background shadow-lg"
            >
              <div
                v-if="isSearchingContacts"
                class="px-3 py-3 text-sm text-n-slate-11"
              >
                {{ $t('KANBAN.DEAL.CONTACT_SEARCH_LOADING') }}
              </div>
              <div
                v-else-if="contactSearchError"
                role="alert"
                class="px-3 py-3 text-sm text-n-ruby-11"
              >
                {{ contactSearchError }}
              </div>
              <div
                v-else-if="!contacts.length"
                class="px-3 py-3 text-sm text-n-slate-11"
              >
                {{ $t('KANBAN.DEAL.CONTACT_SEARCH_EMPTY') }}
              </div>
              <template v-else>
                <button
                  v-for="contact in contacts"
                  :key="contact.id"
                  type="button"
                  class="flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-n-brand/5"
                  @click="selectContact(contact)"
                >
                  <img
                    v-if="contact.thumbnail"
                    :src="contact.thumbnail"
                    class="size-9 rounded-md object-cover"
                    alt=""
                  />
                  <span
                    v-else
                    class="grid size-9 place-content-center rounded-md border border-n-weak bg-transparent text-sm font-semibold text-n-slate-12"
                  >
                    {{ contact.name?.[0]?.toUpperCase() || '?' }}
                  </span>
                  <span class="min-w-0">
                    <span
                      class="block truncate text-sm font-semibold text-n-slate-12"
                    >
                      {{ contact.name }}
                    </span>
                    <span class="block truncate text-xs text-n-slate-11">
                      {{
                        contact.email ||
                        contact.phone_number ||
                        contact.identifier
                      }}
                    </span>
                  </span>
                </button>
              </template>
            </div>
          </div>

          <div class="border-t border-n-weak pt-5">
            <div class="mb-3 flex items-center justify-between gap-3">
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('KANBAN.DEAL.CONTACT_LABELS_TITLE') }}
              </h3>
              <span v-if="isSavingLabels" class="text-xs text-n-slate-10">
                {{ $t('KANBAN.DEAL.SAVING') }}
              </span>
            </div>
            <div v-if="accountLabels.length" class="flex flex-wrap gap-2">
              <button
                v-for="label in accountLabels"
                :key="label.id"
                type="button"
                class="rounded-md border px-2 py-1 text-xs font-semibold transition-colors"
                :class="
                  contactLabels.includes(label.title)
                    ? 'border-n-brand bg-n-brand/10 text-n-brand'
                    : 'border-n-weak bg-transparent text-n-slate-11 hover:border-n-strong hover:bg-n-brand/5'
                "
                :disabled="!contactId"
                @click="toggleContactLabel(label.title)"
              >
                {{ label.title }}
              </button>
            </div>
            <p
              v-else
              class="rounded-lg border border-dashed border-n-weak bg-transparent p-5 text-sm text-n-slate-11"
            >
              {{ $t('KANBAN.DEAL.NO_LABELS') }}
            </p>
          </div>
        </div>

        <div v-else-if="activeTab === 'notes'" class="space-y-4 p-5">
          <div class="border-b border-n-weak pb-5">
            <label class="block">
              <span class="text-sm font-medium text-n-slate-11">
                {{ $t('KANBAN.DEAL.ADD_NOTE_LABEL') }}
              </span>
              <textarea
                v-model="noteContent"
                rows="4"
                class="mt-1 w-full rounded-md border border-n-weak bg-transparent px-4 py-3 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand/60 focus:bg-n-brand/5"
                :placeholder="$t('KANBAN.DEAL.ADD_NOTE_PLACEHOLDER')"
                :disabled="!contactId"
              />
            </label>
            <div class="mt-3 flex justify-end">
              <Button
                type="button"
                sm
                solid
                blue
                icon="i-lucide-send"
                :disabled="!contactId || !noteContent.trim()"
                :is-loading="isCreatingNote"
                :label="$t('KANBAN.DEAL.ADD_NOTE')"
                @click="createNote"
              />
            </div>
          </div>

          <div
            v-if="isFetchingNotes"
            class="rounded-lg border border-n-weak p-4 text-sm text-n-slate-11"
          >
            {{ $t('KANBAN.DEAL.NOTES_LOADING') }}
          </div>
          <div v-else-if="notes.length" class="space-y-3">
            <article
              v-for="note in notes"
              :key="note.id"
              class="rounded-lg border border-n-weak bg-transparent p-4"
            >
              <p class="whitespace-pre-wrap text-sm text-n-slate-12">
                {{ note.content }}
              </p>
              <p class="mt-3 text-xs text-n-slate-10">
                {{ note.user?.name || $t('KANBAN.DEAL.NOTE_AUTHOR_SYSTEM') }}
                {{ $t('KANBAN.SEPARATOR') }}
                {{ formatTimestamp(note.created_at) }}
              </p>
            </article>
          </div>
          <p
            v-else
            class="rounded-lg border border-dashed border-n-weak p-6 text-center text-sm text-n-slate-11"
          >
            {{
              contactId
                ? $t('KANBAN.DEAL.NOTES_EMPTY')
                : $t('KANBAN.DEAL.NOTES_REQUIRE_CONTACT')
            }}
          </p>
        </div>

        <div v-else class="space-y-3 p-5">
          <div
            v-for="conversation in conversations"
            :key="conversation.id"
            class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-n-weak bg-transparent p-4"
          >
            <div class="min-w-0">
              <p class="text-sm font-semibold text-n-slate-12">
                {{
                  $t('KANBAN.DEAL.CONVERSATION_ID', {
                    id: conversation.display_id || conversation.id,
                  })
                }}
              </p>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ conversation.inbox_name || $t('KANBAN.DEAL.NO_INBOX') }}
                {{ $t('KANBAN.SEPARATOR') }}
                {{ conversation.status }}
              </p>
              <div
                v-if="conversation.labels?.length"
                class="mt-2 flex flex-wrap gap-1"
              >
                <span
                  v-for="label in conversation.labels"
                  :key="label"
                  class="rounded border border-n-weak bg-transparent px-1.5 py-0.5 text-xs font-medium text-n-slate-11"
                >
                  {{ label }}
                </span>
              </div>
            </div>
            <Button
              type="button"
              sm
              faded
              blue
              icon="i-lucide-message-square"
              :label="$t('KANBAN.DEAL.OPEN_CONVERSATION')"
              @click="openConversation(conversation)"
            />
          </div>
          <p
            v-if="!conversations.length"
            class="rounded-lg border border-dashed border-n-weak p-6 text-center text-sm text-n-slate-11"
          >
            {{ $t('KANBAN.DEAL.NO_CONVERSATIONS') }}
          </p>
        </div>
      </form>

      <footer
        class="flex items-center justify-between gap-3 border-t border-n-weak px-5 py-4"
      >
        <Button
          v-if="isEditing"
          type="button"
          sm
          ghost
          ruby
          :label="$t('KANBAN.DEAL.DELETE')"
          icon="i-lucide-trash-2"
          @click="deleteDeal"
        />
        <span v-else />
        <div class="flex gap-2">
          <Button
            type="button"
            sm
            ghost
            slate
            :label="$t('KANBAN.DEAL.CANCEL')"
            @click="emit('close')"
          />
          <Button
            sm
            solid
            blue
            type="button"
            :is-loading="isSaving"
            :label="
              isEditing ? $t('KANBAN.DEAL.SAVE') : $t('KANBAN.DEAL.CREATE')
            "
            @click="saveDeal"
          />
        </div>
      </footer>
    </section>
  </div>
</template>
